document.documentElement.classList.remove('no-js');

const themeStatus = () => document.getElementById('ThemeStatus');

/* Compact sticky header after the page scrolls a little. */
(() => {
  const root = document.documentElement;
  let ticking = false;
  const update = () => {
    root.classList.toggle('is-scrolled', window.scrollY > 12);
    ticking = false;
  };
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    },
    { passive: true },
  );
  update();
})();

class MobileNavigation extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.trigger = this.querySelector('[data-mobile-menu-open]');
    this.dialog = this.querySelector('dialog');
    this.closeButton = this.querySelector('[data-mobile-menu-close]');

    if (!this.trigger || !this.dialog || !this.closeButton) return;

    this.trigger.addEventListener('click', () => this.open());
    this.closeButton.addEventListener('click', () => this.dialog.close());
    this.dialog.addEventListener('close', () => this.afterClose());
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.dialog.close();
    });

    this.dialog.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', () => this.dialog.close());
    });
  }

  open() {
    this.trigger.setAttribute('aria-expanded', 'true');
    this.dialog.showModal();
    this.closeButton.focus();
  }

  afterClose() {
    this.trigger.setAttribute('aria-expanded', 'false');
    this.trigger.focus();
  }
}

class QuantityInput extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.input = this.querySelector('input[type="number"]');
    if (!this.input) return;

    this.addEventListener('click', (event) => {
      const button = event.target.closest('button[data-quantity-action]');
      if (!button) return;

      const current = Number(this.input.value) || 1;
      const step = Number(this.input.step) || 1;
      const minimum = Number(this.input.min) || 1;
      const maximum = this.input.max ? Number(this.input.max) : Infinity;
      const direction = button.dataset.quantityAction === 'increase' ? 1 : -1;

      this.input.value = Math.min(maximum, Math.max(minimum, current + step * direction));
      this.input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }
}

class ProductForm extends HTMLElement {
  connectedCallback() {
    if (this.initialized) return;
    this.initialized = true;

    this.form = this.querySelector('form[action*="/cart/add"]');
    this.variantData = this.querySelector('[data-product-variants]');
    this.masterId = this.querySelector('[data-variant-id]');
    this.addButton = this.querySelector('[data-add-to-cart]');
    this.price = this.querySelector('[data-product-price]');
    this.comparePrice = this.querySelector('[data-product-compare-price]');

    if (!this.form || !this.variantData || !this.masterId || !this.addButton) return;

    try {
      this.variants = JSON.parse(this.variantData.textContent);
    } catch (error) {
      return;
    }

    this.querySelectorAll('[data-option-position]').forEach((select) => {
      select.addEventListener('change', () => this.updateVariant());
    });

    this.form.addEventListener('submit', () => {
      if (this.addButton.disabled) return;
      this.addButton.dataset.originalText = this.addButton.textContent;
      this.addButton.textContent = this.dataset.loadingText;
      this.addButton.setAttribute('aria-busy', 'true');
      this.addButton.disabled = true;
    });
  }

  updateVariant() {
    const selectedOptions = Array.from(this.querySelectorAll('[data-option-position]')).map(
      (select) => select.value,
    );
    const variant = this.variants.find((item) =>
      item.options.every((option, index) => option === selectedOptions[index]),
    );

    if (!variant) {
      this.setUnavailable(this.dataset.unavailableText);
      return;
    }

    this.masterId.value = variant.id;
    if (this.price) this.price.textContent = variant.price;

    if (this.comparePrice) {
      this.comparePrice.textContent = variant.compareAtPrice || '';
      this.comparePrice.hidden = !variant.compareAtPrice;
    }

    this.addButton.disabled = !variant.available;
    this.addButton.setAttribute('aria-disabled', String(!variant.available));
    this.addButton.textContent = variant.available ? this.dataset.addText : this.dataset.soldOutText;

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);

    const status = themeStatus();
    if (status) {
      status.textContent = variant.available ? this.dataset.variantAvailableText : this.dataset.soldOutText;
    }
  }

  setUnavailable(message) {
    this.masterId.value = '';
    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-disabled', 'true');
    this.addButton.textContent = message;
  }
}

if (!customElements.get('mobile-navigation')) {
  customElements.define('mobile-navigation', MobileNavigation);
}

if (!customElements.get('quantity-input')) {
  customElements.define('quantity-input', QuantityInput);
}

if (!customElements.get('product-form')) {
  customElements.define('product-form', ProductForm);
}

document.addEventListener('change', (event) => {
  const select = event.target.closest('select[data-auto-submit]');
  if (select?.form) select.form.requestSubmit();
});

document.addEventListener('submit', (event) => {
  const cartForm = event.target.closest('[data-cart-form]');
  if (!cartForm) return;

  cartForm.setAttribute('aria-busy', 'true');
  const status = themeStatus();
  if (status) status.textContent = cartForm.dataset.loadingText;
});

window.addEventListener('pageshow', () => {
  document.querySelectorAll('[data-add-to-cart][data-original-text]').forEach((button) => {
    button.disabled = false;
    button.removeAttribute('aria-busy');
    button.textContent = button.dataset.originalText;
  });
});
